/**
 * Material & geometry kit for the realistic parametric module (Riggdesign 3D, level 1).
 *
 * Everything here is procedural – no downloads, no asset pipeline – so the scene works offline and stays fast:
 *  - a corrugated (trapezoidal) steel cladding rendered as colour + normal + roughness CanvasTextures, tiled in
 *    metres (one tile = one 1.2 m sheet, six 0.2 m ribs, a panel joint at every sheet edge);
 *  - a quiet industrial palette (RAL 7016/7021 anthracite for trims, grey roofing membrane, galvanised steel,
 *    concrete) shared by every module so the rig reads as one product family;
 *  - geometry helpers that merge many small boxes/cylinders into a single BufferGeometry per part, keeping the
 *    draw-call count at ~10 per module even with windows, trims, stairs and railings.
 *
 * Coordinates are module-local: x across the module (width), z along it (length), y up from the module's underside.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

export const TILE = 1.2; // metres covered by one texture tile (standard sheet cover width)
export const FOUNDATION_H = 0.25; // ground-floor modules rest on blocks this high
export const BASE_H = 0.14; // dark floor frame at the foot of every module
export const CAP_H = 0.1; // roof edge flashing
export const CORNER = 0.09; // corner profile width

export const PALETTE = {
  trim: "#3b4046", // RAL 7016 – window frames, doors, roof flashing
  frame: "#2c3035", // RAL 7021 – floor frame, corner profiles
  roof: "#70767d", // roofing membrane
  glass: "#3a4856", // slate glazing – dark with sky reflections
  glassFrost: "#93a5b4", // obscured glass for wet rooms
  galv: "#b6bbc0", // galvanised steel – stairs, railings, roof hoods
  concrete: "#8d8f8d",
  louvre: "#484e56",
  conflict: "#e2a7a7",
};

/* ---------------- procedural cladding textures ---------------- */

const smooth = (t) => t * t * (3 - 2 * t);
/** Trapezoidal rib profile over one period: flank – crest – flank – valley. */
function ribHeight(u) {
  const f = 0.12, c = 0.3;
  if (u < f) return smooth(u / f);
  if (u < f + c) return 1;
  if (u < 2 * f + c) return smooth(1 - (u - f - c) / f);
  return 0;
}
/** Small deterministic PRNG so the grain is identical on every load. */
function lcg(seed) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

let cladding = null;
/** Colour / normal / roughness maps for the corrugated cladding (created once, shared by every module). */
export function claddingTextures() {
  if (cladding) return cladding;
  if (typeof document === "undefined") return null;
  const S = 512, RIBS = 6, AMP = 0.022, dx = TILE / S;
  // height profile (metres) across one tile, with a shallow groove at the sheet joint
  const h = new Float32Array(S);
  for (let x = 0; x < S; x++) {
    const u = ((x / S) * RIBS) % 1;
    let v = ribHeight(u) * AMP;
    if (x < 3) v -= 0.008 * (1 - Math.abs(x - 1) / 2);
    h[x] = v;
  }
  // normal map – tangent space, ribs run along v so only the x-derivative matters
  const nc = document.createElement("canvas");
  nc.width = S; nc.height = 16;
  const nctx = nc.getContext("2d");
  const nimg = nctx.createImageData(S, 16);
  for (let x = 0; x < S; x++) {
    const slope = (h[(x + 1) % S] - h[(x - 1 + S) % S]) / (2 * dx);
    const len = Math.hypot(slope, 1);
    const nx = -slope / len, nz = 1 / len;
    const r = Math.round((nx * 0.5 + 0.5) * 255), b = Math.round((nz * 0.5 + 0.5) * 255);
    for (let y = 0; y < 16; y++) {
      const i = (y * S + x) * 4;
      nimg.data[i] = r; nimg.data[i + 1] = 128; nimg.data[i + 2] = b; nimg.data[i + 3] = 255;
    }
  }
  nctx.putImageData(nimg, 0, 0);
  // colour map – white paint with valley occlusion, sheet joint, faint vertical streaking and grain
  const cc = document.createElement("canvas");
  cc.width = S; cc.height = S;
  const cctx = cc.getContext("2d");
  const cimg = cctx.createImageData(S, S);
  const rnd = lcg(7);
  const streak = new Float32Array(S);
  for (let x = 0; x < S; x++) streak[x] = 1 + (rnd() - 0.5) * 0.035;
  for (let x = 0; x < S; x++) {
    const depth = 1 - h[x] / AMP; // 0 on the crest, 1 in the valley
    let base = (1 - 0.075 * Math.max(0, Math.min(1, depth))) * streak[x];
    if (x < 1) base *= 0.8; else if (x === 1) base *= 1.03;
    for (let y = 0; y < S; y++) {
      const i = (y * S + x) * 4;
      const v = Math.max(0, Math.min(255, Math.round(base * (1 + (rnd() - 0.5) * 0.02) * 255)));
      cimg.data[i] = v; cimg.data[i + 1] = v; cimg.data[i + 2] = v; cimg.data[i + 3] = 255;
    }
  }
  cctx.putImageData(cimg, 0, 0);
  // roughness map – crests a touch glossier than valleys (green channel is read)
  const rc = document.createElement("canvas");
  rc.width = S; rc.height = 16;
  const rctx = rc.getContext("2d");
  const rimg = rctx.createImageData(S, 16);
  for (let x = 0; x < S; x++) {
    const depth = 1 - h[x] / AMP;
    const rough = Math.round((0.5 + 0.14 * Math.max(0, Math.min(1, depth)) + (x < 2 ? 0.2 : 0)) * 255);
    for (let y = 0; y < 16; y++) {
      const i = (y * S + x) * 4;
      rimg.data[i] = rough; rimg.data[i + 1] = rough; rimg.data[i + 2] = rough; rimg.data[i + 3] = 255;
    }
  }
  rctx.putImageData(rimg, 0, 0);

  const mk = (canvas, srgb) => {
    const t = new THREE.CanvasTexture(canvas);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.anisotropy = 8;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = true;
    t.needsUpdate = true;
    return t;
  };
  cladding = { map: mk(cc, true), normalMap: mk(nc, false), roughnessMap: mk(rc, false) };
  return cladding;
}

/* ---------------- geometry helpers ---------------- */

/**
 * BoxGeometry whose UVs are in metres (÷ TILE) per face, so the cladding tiles at true scale on every side and
 * ribs stay vertical. Face order in BoxGeometry: +x, −x, +y, −y, +z, −z; each face has four vertices.
 */
export function metricBox(w, h, l, tile = TILE) {
  const g = new THREE.BoxGeometry(w, h, l);
  const uv = g.attributes.uv;
  const faces = [[l, h], [l, h], [w, l], [w, l], [w, h], [w, h]];
  for (let f = 0; f < 6; f++) {
    const [fu, fv] = faces[f];
    for (let i = f * 4; i < f * 4 + 4; i++) uv.setXY(i, (uv.getX(i) * fu) / tile, (uv.getY(i) * fv) / tile);
  }
  uv.needsUpdate = true;
  return g;
}

/** Merge a list of positioned primitives into one geometry. Items: {g: BufferGeometry, p: [x,y,z], r?: [x,y,z]}. */
export function merge(items) {
  const gs = [];
  for (const it of items) {
    if (!it) continue;
    const g = it.g;
    if (it.r) g.rotateX(it.r[0]).rotateY(it.r[1]).rotateZ(it.r[2]);
    g.translate(it.p[0], it.p[1], it.p[2]);
    gs.push(g);
  }
  if (!gs.length) return null;
  if (gs.length === 1) return gs[0];
  // mergeGeometries needs all-indexed or all-non-indexed input (RoundedBoxGeometry is non-indexed)
  const mixed = gs.some((g) => !g.index) && gs.some((g) => g.index);
  const src = mixed ? gs.map((g) => (g.index ? g.toNonIndexed() : g)) : gs;
  const out = mergeGeometries(src, false);
  gs.forEach((g) => g.dispose());
  if (mixed) src.forEach((g) => { if (!gs.includes(g)) g.dispose(); });
  return out;
}
export const box = (w, h, l, x, y, z) => ({ g: new THREE.BoxGeometry(w, h, l), p: [x, y, z] });
export const cyl = (r, h, x, y, z, rot) => ({ g: new THREE.CylinderGeometry(r, r, h, 10), p: [x, y, z], r: rot });

/* ---------------- part specifications per module kind ---------------- */

/** Window opening per kind – width, height and sill height above the module underside (floor is ~0.15 up). */
export const WINDOW_SPEC = {
  bolig: { w: 1.1, h: 1.15, sill: 1.1 },
  kontor: { w: 1.35, h: 1.3, sill: 1.0 },
  kantine: { w: 1.7, h: 1.45, sill: 0.9 },
  felles: { w: 1.7, h: 1.45, sill: 0.9 },
  sanitaer: { w: 0.8, h: 0.55, sill: 1.8, frosted: true },
  vaskeri: { w: 0.9, h: 0.6, sill: 1.75, frosted: true },
  garderobe: { w: 0.9, h: 0.6, sill: 1.75, frosted: true },
  annet: { w: 1.1, h: 1.15, sill: 1.1 },
};
/** Door leaf width per kind (lager gets container-style double doors). */
export const DOOR_W = { lager: 2.2, teknisk: 1.1, default: 0.95 };

/* ---------------- site dressing: ground, fence mesh ---------------- */

/** Ground surfaces for the rig pad. `tile` is the metres covered by one repeat. */
export const GROUND_SPEC = {
  asfalt: { tile: 3.0, roughness: 0.96, label: "Asfalt" },
  grus: { tile: 2.0, roughness: 1.0, label: "Grus" },
};
const groundCache = {};
function wrapDist(ax, ay, bx, by, S) {
  let dx = Math.abs(ax - bx), dy = Math.abs(ay - by);
  if (dx > S / 2) dx = S - dx;
  if (dy > S / 2) dy = S - dy;
  return Math.hypot(dx, dy);
}
/** Seamless procedural asphalt (fine aggregate + soft wear blotches) or gravel (speckled stones) colour map. */
export function groundTexture(kind) {
  if (groundCache[kind]) return groundCache[kind];
  if (typeof document === "undefined") return null;
  const S = 512;
  const c = document.createElement("canvas");
  c.width = S; c.height = S;
  const ctx = c.getContext("2d");
  const rnd = lcg(kind === "grus" ? 23 : 17);
  const img = ctx.createImageData(S, S);
  if (kind === "grus") {
    for (let i = 0; i < S * S; i++) {
      const n = 1 + (rnd() - 0.5) * 0.18;
      img.data[i * 4] = Math.round(178 * n); img.data[i * 4 + 1] = Math.round(173 * n); img.data[i * 4 + 2] = Math.round(163 * n); img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    for (let i = 0; i < 9000; i++) {
      const x = rnd() * S, y = rnd() * S, r = 0.8 + rnd() * 2.6;
      const v = 95 + Math.round(rnd() * 120), warm = rnd() < 0.25;
      ctx.fillStyle = `rgb(${v + (warm ? 14 : 0)},${v + (warm ? 6 : 0)},${v - (warm ? 8 : 0)})`;
      for (const [ox, oy] of [[0, 0], [S, 0], [-S, 0], [0, S], [0, -S]]) { ctx.beginPath(); ctx.ellipse(x + ox, y + oy, r, r * (0.6 + rnd() * 0.4), rnd() * Math.PI, 0, Math.PI * 2); ctx.fill(); }
    }
  } else {
    const blobs = [];
    for (let i = 0; i < 26; i++) blobs.push({ x: rnd() * S, y: rnd() * S, r: 40 + rnd() * 110, d: (rnd() - 0.5) * 0.09 });
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
      let v = 0.27 * (1 + (rnd() - 0.5) * 0.22);
      for (const b of blobs) { const d = wrapDist(x, y, b.x, b.y, S); if (d < b.r) v += b.d * (1 - d / b.r); }
      if (rnd() < 0.006) v += 0.14;
      const g = Math.max(0, Math.min(255, Math.round(v * 255)));
      const i = (y * S + x) * 4;
      img.data[i] = g; img.data[i + 1] = g + 1; img.data[i + 2] = g + 3; img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  t.generateMipmaps = true;
  t.repeat.set(1 / GROUND_SPEC[kind].tile, 1 / GROUND_SPEC[kind].tile); // pad UVs are in metres
  t.needsUpdate = true;
  groundCache[kind] = t;
  return t;
}

export const FENCE_TILE = 0.5; // metres per mesh-texture repeat
let fenceTex = null;
/** Alpha map for a welded-mesh fence panel (100 × 250 mm mesh). */
export function fenceMeshTexture() {
  if (fenceTex) return fenceTex;
  if (typeof document === "undefined") return null;
  const S = 256;
  const c = document.createElement("canvas");
  c.width = S; c.height = S;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, S, S);
  ctx.fillStyle = "#ffffff";
  const wire = 2.2;
  for (let i = 0; i < 5; i++) ctx.fillRect(Math.round((i * S) / 5) - wire / 2, 0, wire, S); // vertical wires every 0.1 m
  for (let i = 0; i < 2; i++) ctx.fillRect(0, Math.round((i * S) / 2) - wire / 2, S, wire); // horizontal every 0.25 m
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.NoColorSpace;
  t.anisotropy = 8;
  t.needsUpdate = true;
  fenceTex = t;
  return t;
}

/** PlaneGeometry (w × h, facing +z) with UVs in metres ÷ tile. */
export function metricPlane(w, h, tile) {
  const g = new THREE.PlaneGeometry(w, h);
  const uv = g.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) * w) / tile, (uv.getY(i) * h) / tile);
  uv.needsUpdate = true;
  return g;
}

/* ---------------- weathering (procedural alpha maps) ---------------- */

let grimeCache = null;
/** Alpha maps for dirt: `splash` (ground splash zone, dark at the bottom) and `streak` (run-off streaks below sills). */
export function grimeTextures() {
  if (grimeCache) return grimeCache;
  if (typeof document === "undefined") return null;
  const mk = (w, h, paint) => {
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    const img = ctx.createImageData(w, h);
    paint(img.data, w, h);
    ctx.putImageData(img, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.colorSpace = THREE.NoColorSpace;
    t.needsUpdate = true;
    return t;
  };
  const rnd = lcg(41);
  const splash = mk(128, 128, (d, w, h) => {
    const col = new Float32Array(w);
    for (let x = 0; x < w; x++) col[x] = 0.75 + rnd() * 0.5;
    for (let y = 0; y < h; y++) {
      const t = 1 - y / (h - 1); // 1 at the bottom row (canvas y grows downwards; v=0 is the bottom after flipY)
      for (let x = 0; x < w; x++) {
        const a = Math.pow(Math.max(0, t - 0.05), 1.6) * col[x] * (0.85 + rnd() * 0.3);
        const i = (y * w + x) * 4;
        const v = Math.round(Math.min(1, a) * 255);
        d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 255;
      }
    }
  });
  const streak = mk(128, 192, (d, w, h) => {
    // a few run-off streaks of varying width, strongest at the top (just under the sill), fading downwards
    const streaks = [];
    for (let k = 0; k < 6; k++) streaks.push({ x: 0.12 + rnd() * 0.76, w: 0.012 + rnd() * 0.035, s: 0.5 + rnd() * 0.5, len: 0.45 + rnd() * 0.55 });
    for (let y = 0; y < h; y++) {
      const v = 1 - y / (h - 1); // v: 1 at top row
      for (let x = 0; x < w; x++) {
        const u = x / (w - 1);
        let a = 0;
        for (const s of streaks) {
          const dx = Math.abs(u - s.x);
          if (dx < s.w) a += s.s * (1 - dx / s.w) * Math.max(0, 1 - (1 - v) / s.len);
        }
        a += Math.max(0, v - 0.85) * 2.5; // dark line right under the sill
        const i = (y * w + x) * 4;
        const px = Math.round(Math.min(1, a * (0.8 + rnd() * 0.4)) * 255);
        d[i] = px; d[i + 1] = px; d[i + 2] = px; d[i + 3] = 255;
      }
    }
  });
  grimeCache = { splash, streak };
  return grimeCache;
}

/* ---------------- photo textures (vendored CC0, see assets3d.manifest.json) ---------------- */

/**
 * Loads the photo maps once, one group at a time (cladding / asphalt / gravel / terrain). Each group resolves only when
 * every one of its maps is ready, so materials never bind a texture that has no image yet (which would render black /
 * broken normals for a frame). A failing group becomes `null` → that surface keeps its procedural fallback while the
 * others still get their photo maps. Consumers receive `null` for the whole set until the first pass completes.
 */
let photoPromise = null;
export function loadPhotoMaps(manifest) {
  if (photoPromise) return photoPromise;
  const loader = new THREE.TextureLoader();
  const load = (url, { srgb = false, repeat = 1 } = {}) => new Promise((resolve, reject) => {
    loader.load(url, (t) => {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.anisotropy = 8;
      t.repeat.set(repeat, repeat);
      t.needsUpdate = true;
      resolve(t);
    }, undefined, reject);
  });
  const T = manifest.textures || {};
  const group = (id, spec, repeat) => {
    const tex = T[id];
    if (!tex) return Promise.resolve(null);
    const rep = repeat ?? 1 / tex.dims_m[0];
    const entries = Object.entries(spec).filter(([, key]) => tex.maps[key]);
    return Promise.all(entries.map(([, key]) => load(tex.maps[key], { srgb: key === "Diffuse", repeat: rep })))
      .then((maps) => Object.fromEntries(entries.map(([slot], i) => [slot, maps[i]])))
      .catch((e) => { console.warn(`Fototekstur «${id}» kunne ikke lastes – bruker prosedyral`, e?.message || e); return null; });
  };
  const clad = T.box_profile_metal_sheet;
  photoPromise = Promise.all([
    group("box_profile_metal_sheet", { normalMap: "nor_gl", roughnessMap: "Rough", aoMap: "AO" }, clad ? TILE / clad.dims_m[0] : 1), // body UVs are in units of TILE metres
    group("asphalt_02", { map: "Diffuse", normalMap: "nor_gl", roughnessMap: "Rough" }),
    group("gravel_floor_02", { map: "Diffuse", normalMap: "nor_gl" }),
    group("aerial_grass_rock", { map: "Diffuse", normalMap: "nor_gl" }),
  ]).then(([cladding, asfalt, grus, terreng]) => ({ cladding, asfalt, grus, terreng }));
  return photoPromise;
}
